import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ShortySeekBar from "./ShortySeekBar";
import { createEvent } from "core/utils/tracker";
import useIdleRef from "core/hooks/useIdleRef";
import Shortcut from "core/components/Shortcut";
import BigPlayButton from "core/components/BigPlayButton";
import VideoLoading from "core/components/VideoLoading";
import ShortyContentInfo from "./ShortyContentInfo";
import ShortyControlPanel from "./ShortyControlPanel";
import ShortyCommentsDrawer from "./ShortyCommentsDrawer";
import ThumbnailBackground from "./ThumbnailBackground";

export default function ShortyVideoContent({
  videoElement,
  videoState,
  videoActions,
  videoRef,
  content,
  closeRouteDialogButton,
  showThumbnail,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  preload = false, // eslint-disable-line react/prop-types
  shouldStart = true, // eslint-disable-line react/prop-types
  onVideoActionsReady, // eslint-disable-line react/prop-types
}) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const idle = useIdleRef({ ref: videoRef });
  const visible = !idle;
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Эффект для передачи videoActions родителю для предзагрузки
  React.useEffect(() => {
    if (onVideoActionsReady && videoActions && preload && !shouldStart) {
      onVideoActionsReady(videoActions);
    }
  }, [onVideoActionsReady, videoActions, preload, shouldStart]);

  // Эффект для старта предзагруженного видео
  React.useEffect(() => {
    if (shouldStart && !hasStarted && videoActions && preload) {
      // Если shouldStart=true, еще не стартовали, есть videoActions и был preload
      const timer = setTimeout(() => {
        if (videoActions?.play) {
          videoActions.play();
          setHasStarted(true);
        }
      }, 400); // Задержка для плавности скролла

      return () => clearTimeout(timer);
    }
  }, [shouldStart, hasStarted, videoActions, preload]);

  // Сброс hasStarted при смене контента или режима
  React.useEffect(() => {
    if (!preload || !shouldStart) {
      setHasStarted(false);
    }
  }, [preload, shouldStart, content?.id]);

  // Обработчик клика для play/pause
  const onClickBigPlay = () => {
    videoActions.togglePlayUser();
    if (videoState.status === "playing") {
      createEvent("video", "pause");
    } else {
      createEvent("video", "play");
    }
  };

  // Логика видимости контролов (упрощенная для Shorty)
  const showControls = visible || videoState.isUserPause;

  // Обработчики комментариев
  const handleOpenComments = () => {
    setCommentsOpen(true);
  };

  const handleCloseComments = () => {
    setCommentsOpen(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%", // Наследуем высоту от родительского контейнера
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {isMobileView ? (
        // Полноэкранный режим для мобилки (как было в моих изменениях)
        <Box
          sx={{
            width: "100%", // Наследуем ширину от родителя
            height: "100%", // Наследуем высоту от родителя
            position: "relative",
          }}
        >
          {/* Панель управления справа от плеера */}
          <ShortyControlPanel
            content={content}
            videoState={videoState}
            videoActions={videoActions}
            videoElement={videoElement}
            onPrevious={onPrevious}
            onNext={onNext}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            onOpenComments={handleOpenComments}
          />
          <Box
            id="player"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              overflow: "hidden",
              backgroundColor: "#000", // Черный фон
            }}
          >
            {/* Видео элемент всегда рендерится, но может быть скрыт */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: showThumbnail ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
                // Стили для cover fit видео на весь экран
                "& video": {
                  width: "100% !important",
                  height: "100% !important",
                  objectFit: "cover !important",
                  objectPosition: "center center !important",
                },
              }}
            >
              {videoElement}
            </Box>

            {/* Показываем thumbnail поверх видео пока оно загружается */}
            {showThumbnail && (
              <ThumbnailBackground
                content={content}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 1,
                  transition: "opacity 0.3s ease-in-out",
                  "& img": {
                    width: "100% !important",
                    height: "100% !important",
                    objectFit: "cover !important",
                    objectPosition: "center center !important",
                  },
                }}
              />
            )}

            <VideoLoading videoState={videoState} />

            <Shortcut videoActions={videoActions} videoState={videoState} />

            {/* BigPlayButton для паузы/воспроизведения */}
            {showControls && (
              <BigPlayButton onClick={onClickBigPlay} videoState={videoState} />
            )}

            {/* CloseRouteDialogButton в левом верхнем углу - плавное появление */}
            {closeRouteDialogButton && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 10,
                }}
              >
                {closeRouteDialogButton}
              </Box>
            )}

            {/* Название видео и описание - такое же как в плеере */}
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                left: 0,
                right: 0,
              }}
            >
              <ShortyContentInfo
                content={content}
                titleVariant="h6"
                descriptionVariant="body2"
                sx={{
                  px: 1,
                  // Небольшой отступ справа для индикатора просмотра
                  pr: { xs: 3, sm: 3, md: 1 },
                }}
              />
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: -23,
                left: 0,
                right: 0,
              }}
            >
              <Box
                sx={{
                  opacity: showControls ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                  position: "relative",
                  zIndex: 25, // Выше чем у ShortyControlPanel (zIndex: 20)
                }}
              >
                <ShortySeekBar
                  videoState={videoState}
                  videoActions={videoActions}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // Десктопный режим - адаптируется к размеру родительского контейнера
        <Box
          sx={{
            width: "100%",
            height: "100%", // Занимает всю доступную высоту от ShortyVirtualFeed
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "auto",
              height: "100%",
              aspectRatio: "9/16", // Соотношение сторон 9:16
              maxWidth: "100%",
              position: "relative",
            }}
          >
            {/* Панель управления справа от плеера */}
            <ShortyControlPanel
              content={content}
              videoState={videoState}
              videoActions={videoActions}
              videoElement={videoElement}
              onPrevious={onPrevious}
              onNext={onNext}
              canGoPrevious={canGoPrevious}
              canGoNext={canGoNext}
              onOpenComments={handleOpenComments}
            />
            <Box
              id="player"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                overflow: "hidden",
                backgroundColor: "#000",
              }}
            >
              {/* Видео элемент всегда рендерится, но может быть скрыт */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: showThumbnail ? 0 : 1,
                  transition: "opacity 0.3s ease-in-out",
                }}
              >
                {videoElement}
              </Box>

              {/* Показываем thumbnail поверх видео пока оно загружается */}
              {showThumbnail && (
                <ThumbnailBackground
                  content={content}
                  sx={{
                    opacity: 1,
                    transition: "opacity 0.3s ease-in-out",
                  }}
                />
              )}

              <VideoLoading videoState={videoState} />

              <Shortcut videoActions={videoActions} videoState={videoState} />

              {/* BigPlayButton для паузы/воспроизведения */}
              {showControls && (
                <BigPlayButton
                  onClick={onClickBigPlay}
                  videoState={videoState}
                />
              )}

              {/* CloseRouteDialogButton в левом верхнем углу - плавное появление */}
              {closeRouteDialogButton && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 10,
                  }}
                >
                  {closeRouteDialogButton}
                </Box>
              )}

              {/* Название видео и описание - такое же как в плеере */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                }}
              >
                <ShortyContentInfo
                  content={content}
                  titleVariant="h6"
                  descriptionVariant="body2"
                  sx={{
                    px: 1,
                    // Небольшой отступ справа для индикатора просмотра
                    pr: { xs: 3, sm: 3, md: 1 },
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  left: 0,
                  right: 0,
                }}
              >
                <Box
                  sx={{
                    opacity: showControls ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    position: "relative",
                    zIndex: 25, // Выше чем у ShortyControlPanel (zIndex: 20)
                  }}
                >
                  <ShortySeekBar
                    videoState={videoState}
                    videoActions={videoActions}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Комментарии в SwipeableDrawer */}
      <ShortyCommentsDrawer
        content={content}
        open={commentsOpen}
        onClose={handleCloseComments}
        onOpen={handleOpenComments}
      />
    </Box>
  );
}

ShortyVideoContent.propTypes = {
  videoElement: PropTypes.shape({
    ref: PropTypes.object,
  }),
  videoState: PropTypes.shape({
    status: PropTypes.string,
    duration: PropTypes.number,
    currentTime: PropTypes.number,
    buffered: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    isUserPause: PropTypes.bool,
    volume: PropTypes.number,
    muted: PropTypes.bool,
    isLoading: PropTypes.bool,
  }),
  videoActions: PropTypes.shape({
    togglePlayUser: PropTypes.func,
    play: PropTypes.func,
    pause: PropTypes.func,
    navigate: PropTypes.func,
    setVolume: PropTypes.func,
    toggleMute: PropTypes.func,
  }),
  videoRef: PropTypes.object,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    isCommentsEnabled: PropTypes.bool,
    sport: PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
    }),
    league: PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
    }),
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    date: PropTypes.string,
    verticalThumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        webpUrl: PropTypes.string,
      })
    ),
    thumbnails: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        webpUrl: PropTypes.string,
      })
    ),
  }),
  closeRouteDialogButton: PropTypes.node,
  showThumbnail: PropTypes.bool,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  canGoPrevious: PropTypes.bool,
  canGoNext: PropTypes.bool,
};
